import { useState, useEffect, useCallback } from 'react';

interface UsePushNotificationsResult {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  showNotification: (title: string, options?: NotificationOptions) => void;
}

// #2099 — OPT-OUT flag, deliberately inverted from the old rdm_push_subscription
// opt-in. There is no real push subscription to track yet (#2100), so the OS
// permission IS the source of truth: once granted, the toggle is ON unless the
// rep explicitly turned it off. The old opt-in flag was wiped by Reset Session
// (not in PRESERVED_KEYS) and Hard Reset (localStorage.clear()), silently
// flipping the toggle off after every recovery — with an opt-out, a wipe means
// "back to on", which is the desired default.
const OPTOUT_KEY = 'rdm_push_optout';

export function usePushNotifications(): UsePushNotificationsResult {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('unsupported');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;

  // Check initial permission state. Subscribed = permission granted AND no
  // explicit opt-out — so a localStorage wipe (Reset Session / Hard Reset)
  // restores ON instead of silently disabling notifications (#2099).
  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
      setIsSubscribed(Notification.permission === 'granted' && localStorage.getItem(OPTOUT_KEY) !== 'true');
    }
  }, [isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        return true;
      } else if (result === 'denied') {
        setError('Notification permission was denied. Please enable it in your browser settings.');
        return false;
      } else {
        setError('Notification permission was dismissed');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request permission');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications are not supported');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First, ensure we have permission
      if (Notification.permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) return false;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // For full push notification support, you would subscribe to a push service here
      // This requires a VAPID key from your server:
      // const subscription = await registration.pushManager.subscribe({
      //   userVisibleOnly: true,
      //   applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      // });
      // Then send the subscription to your server

      // No real push service yet (#2100) — clearing the opt-out is all that's
      // needed; permission='granted' is the durable source of truth.
      localStorage.removeItem(OPTOUT_KEY);
      setIsSubscribed(true);

      // Show a test notification
      showNotification('Notifications Enabled', {
        body: 'You will now receive schedule updates and alerts.',
        icon: '/icons/icon-192.svg',
        badge: '/icons/icon-192.svg',
      });

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, requestPermission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // For full push notifications, you would unsubscribe from the push service
      // here (#2100). The explicit opt-out is the only thing that keeps the
      // toggle off while the OS permission remains granted.
      localStorage.setItem(OPTOUT_KEY, 'true');
      setIsSubscribed(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Show a local notification
  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!isSupported || Notification.permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted');
      return;
    }

    // Try to use service worker for notification (more reliable)
    navigator.serviceWorker.ready.then((registration) => {
      // Service worker notifications support additional options like vibrate
      registration.showNotification(title, {
        icon: '/icons/icon-192.svg',
        badge: '/icons/icon-192.svg',
        vibrate: [200, 100, 200],
        ...options,
      } as NotificationOptions & { vibrate?: number[] });
    }).catch(() => {
      // Fallback to regular notification
      new Notification(title, {
        icon: '/icons/icon-192.svg',
        ...options,
      });
    });
  }, [isSupported]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    showNotification,
  };
}

// Schedule notification helper - can be used for local reminders
export function scheduleNotification(
  title: string,
  options: NotificationOptions & { delay: number }
): number {
  const { delay, ...notificationOptions } = options;

  const timeoutId = window.setTimeout(() => {
    if (Notification.permission === 'granted') {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, notificationOptions);
      }).catch(() => {
        new Notification(title, notificationOptions);
      });
    }
  }, delay);

  return timeoutId;
}

// Cancel scheduled notification
export function cancelScheduledNotification(timeoutId: number): void {
  window.clearTimeout(timeoutId);
}
